import { Intro, StatStrip, Friction, HeroStat, Callout, Takeaway, SectionLead, Disclaimer, Shot } from './components.jsx';

export const meta = {
  slug: 'mysecretcase',
  metaTitle: 'How MySecretCase Avoided €40K in Shopify Return Costs | Account Editor',
  metaDescription:
    'See how MySecretCase used self-service cancellations and address validation to avoid roughly €40K in return costs across 5,305 orders.',
  brand: 'MySecretCase',
  industry: 'Intimate Wellness · Shopify Plus',
  statNum: '~€40K',
  statDesc: 'Return costs avoided · 5,305 orders self-served',
  detail:
    'How MySecretCase used self-service cancellations and address validation to retain revenue, protect customer privacy, and avoid roughly €40K in return costs across 5,305 orders.',
  quote:
    '"Support has been amazing, super responsive and always quick to help. They consistently implement what we need without any hassle."',
  author: 'MySecretCase Team',
  role: "Italy's Leading Intimate-Wellness Retailer · Milan",
  img: 'MySecretCase',
  hero: 'MySecretCase+Case+Study',
  image: '/images/case-studies/mysecretcase-case-study.webp',
  readTime: '5 min read',
  date: 'May 25, 2026'
};

export default function Content() {
  return (
    <>
      <Intro>
        A refund is money walking out the door. MySecretCase gave customers a reason to keep it
        in-store — and at the same time, made sure the wrong address never shipped on a
        discretion-first order.
      </Intro>

      <StatStrip
        items={[
          { num: '~€40K', label: 'est. return costs avoided', highlight: true },
          { num: '5,305', label: 'orders self-served' },
          { num: '3,061', label: 'cancellations handled' },
          { num: '2,229', label: 'addresses corrected before shipping' },
        ]}
        period="Jul 2025 – May 2026 · ~11 months"
      />

      <SectionLead eyebrow="About the merchant" title="A discretion-first brand at serious scale.">
        Founded in Milan in 2014, MySecretCase is Italy's leading intimate-wellness retailer —
        ~19,000 orders a month on Shopify Plus, every parcel shipped in anonymous packaging. Two
        post-purchase moments mattered most: cancellation (money out) and address accuracy
        (privacy on the line).
      </SectionLead>

      <SectionLead eyebrow="Keep the sale" title="The cancellation will happen. The refund doesn't have to be cash.">
        Standard refund flows treat cancellation as money out the door. MySecretCase used Account Editor
        to convert that moment into a retention lever with a custom store-credit incentive.
      </SectionLead>

      <Friction
        question="I want to cancel — give me my money back."
        oldWay="Cash refund of €100 leaves the business. The customer leaves too. Reporting shows a sale and a refund."
        newWay="Customer picks: cash refund €100, or store credit €101 (+€1 bonus). Revenue stays in-store and Net Sales reports correctly."
        callout="Customers self-cancelled <strong>3,061</strong> orders — many converted to store credit instead of cash."
      />

      <Callout>
        Account Editor also corrected a silent reporting error — store-credit cancellations now adjust
        Net Sales in Shopify financials, so the books match reality.
      </Callout>

      <SectionLead eyebrow="Protect the privacy" title="For a discretion-first brand, the address is the trust.">
        Anonymous packaging only works if it ends up at the right door. Misdelivery isn't a
        logistics problem here — it's a brand problem. Customers needed to fix their own
        addresses, fast, with validation that the fix actually delivers.
      </SectionLead>

      <Friction
        question="My address is wrong — fix it before the parcel ships."
        oldWay="Email support, hope the change lands before pick-and-pack. If it doesn't: a misdelivered, discretion-sensitive parcel."
        newWay="Customer corrects the address on the order page with validation — the warehouse only ever sees the deliverable version."
        callout="Customers corrected <strong>2,229</strong> addresses before shipping — protecting both deliverability and discretion."
      />

      <SectionLead eyebrow="The result" title="Revenue retained. Privacy protected. Reporting fixed.">
        Across ~11 months, Account Editor's measurable footprint spanned cancellation retention, returns
        avoidance, and a silent financial-reporting bug — all without adding to the support queue.
      </SectionLead>

      <Shot
        src="/images/case-studies/mysecretcase-cancel-deflection.webp"
        alt="MySecretCase cancel flow offering store credit instead of a cash refund on the order status page"
        caption="A cancellation becomes store credit, not a cash refund — revenue retained at the moment of intent."
      />

      <HeroStat
        num="~€40K"
        label="estimated return costs avoided"
        period="Jul 2025 – May 2026"
        breakdown={[
          { label: 'Addresses corrected', num: '2,229', note: 'validated before shipment' },
          { label: 'Self-cancellations', num: '3,061', note: 'with store-credit option' },
        ]}
      />

      <Takeaway footnote="Measured across the MySecretCase deployment · Jul 2025 – May 2026">
        <strong>5,305</strong> orders served by customers themselves. <strong>~€40K</strong> in
        avoided returns. A smoother operation across cancellation, delivery, and finance — without
        compromising the discretion the brand is built on.
      </Takeaway>

      <Disclaimer>
        Figures cover Jul 2025 – May 2026. Return cost estimate is modelled from 2,229 corrected
        addresses at MySecretCase's blended per-return cost (forward shipping, return logistics,
        repackaging, reship). Cancellation, address-correction, and detail-fix counts are measured
        directly from the Account Editor deployment.
      </Disclaimer>
    </>
  );
}
